import {
  Modal,
  ModalContent,
  ModalOverlay,
  Input,
  ModalBody,
  ModalCloseButton,
  Stack,
  HStack,
  Spinner,
  Box,
  Flex,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { RiMessage3Line } from 'react-icons/ri';
import { HelivanSelector } from '../helivan/HelivanSelector';
import {
  ConversationType,
  useStartConversationMutation,
  useGetAiMessageResponseMutation,
  useSendAiMessageMutation,
  ConversationsDocument,
  useGetAllDigitalTwinsBasicDataLazyQuery,
  useGetDtTypeAccessQuery,
  DtType,
} from '../../generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { MdOutlinePeople, MdOutlinePerson } from 'react-icons/md';
import { TbWorld } from 'react-icons/tb';
import { HelivanColor } from '../helivan/HelivanColor';
import PersonaSelector from '../Projects/PersonaSelector';
import DemographicSelector from '../Projects/DemographicSelector';
import { IoArrowUpCircleOutline } from 'react-icons/io5';
import CreateChatLoading from '../Projects/CreateChatLoading';
import { useNavigate } from 'react-router-dom';
import { getDtTypeString } from '../../utils/getDtTypeString';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

interface PersonaType {
  id: string;
  name: string;
}

export const CreateConversationModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [selectedDtType, setSelectedDtType] = useState<DtType | undefined>(
    undefined
  );
  const { data: dtTypeAccessData, loading: dtTypeAccessLoading } =
    useGetDtTypeAccessQuery();
  const dtTypes = dtTypeAccessData?.getDtTypeAccess ?? [];
  const [
    getAllDigitalTwinsBasicData,
    { loading: digitalTwinsLoading },
  ] = useGetAllDigitalTwinsBasicDataLazyQuery();
  const navigate = useNavigate();
  const [chatType, setChatType] = useState<ConversationType>(
    ConversationType.Persona
  );
  const [digitalTwins, setDigitalTwins] = useState<PersonaType[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<PersonaType[]>([]);
  const [selectedDemographics, setSelectedDemographics] = useState<
    { key: string; value: string }[]
  >([]);
  const [activeMessage, setActiveMessage] = useState('');

  const [createConversation, { loading: createConversationLoading }] =
    useStartConversationMutation();
  const [sendMessage, { loading: sendMessageLoading }] =
    useSendAiMessageMutation();
  const [getResponse, { loading: getResponseLoading }] =
    useGetAiMessageResponseMutation();

  const handleSelectionChange = useCallback((newSelection: PersonaType[]) => {
    setSelectedPersonas([...newSelection]);
  }, []);

  const handleDemographicChange = useCallback(
    (demographics: { key: string; value: string }[]) => {
      setSelectedDemographics(demographics);
    },
    []
  );

  useEffect(() => {
    if (selectedDtType) {
      getAllDigitalTwinsBasicData({
        variables: {
          type: selectedDtType,
        },
        onCompleted: (data) => {
          setDigitalTwins(
            data.getAllDigitalTwinsBasicData?.map((dt) => ({
              id: dt.id,
              name: dt.name,
            })) ?? []
          );
        },
      });
    }
  }, [selectedDtType]);

  const switchSelectionType = (type: ConversationType) => {
    handleSelectionChange([]);
    setSelectedDemographics([]);
    setChatType(type);
  };

  const getDemographicTwins = (): string[] => {
    if (!selectedDemographics || selectedDemographics.length === 0) {
      return [];
    }

    return selectedDemographics.map((demo) => demo.value);
  };

  const handleCreateAndSend = () => {
    if (activeMessage.length === 0) {
      return;
    }

    createConversation({
      variables: {
        digitalTwinIds:
          chatType === ConversationType.Demographic
            ? getDemographicTwins()
            : selectedPersonas.map((p) => p.id),
        projectId: undefined,
        type: chatType,
        demographic:
          chatType === ConversationType.Demographic
            ? selectedDemographics.map((demo) => demo.value).join(', ')
            : undefined,
      },
      refetchQueries: [
        {
          query: ConversationsDocument,
        },
      ],
      onCompleted: (response) => {
        const conversationId = response.startConversation.id;
        sendMessage({
          variables: {
            message: activeMessage,
            conversationId,
          },
          onCompleted: (data) => {
            getResponse({
              variables: {
                conversationId,
              },
              onCompleted: (data) => {
                if (data.getAIMessageResponse.messages) {
                  // Reset state and close modal
                  setActiveMessage('');
                  setSelectedPersonas([]);
                  setSelectedDemographics([]);
                  setChatType(ConversationType.Persona);
                  navigate(`/conversations/${conversationId}`);
                  onClose();
                }
              },
            });
          },
        });
      },
    });
  };

  const canSubmit =
    activeMessage.length > 0 &&
    ((chatType === ConversationType.Demographic &&
      selectedDemographics.length > 0) ||
      (chatType === ConversationType.Panel && selectedPersonas.length >= 2) ||
      (chatType === ConversationType.Persona && selectedPersonas.length === 1));

  const isLoading =
    getResponseLoading || sendMessageLoading || createConversationLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={4}
        overflow="visible"
        maxH="85vh"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius="28px"
          bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          display="flex"
          flexDirection="column"
          height="600px"
          maxH="85vh"
        >
          {/* Header with gradient accent */}
          <Box position="relative" overflow="hidden" flexShrink={0}>
            {/* Gradient bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, #00477D, #486884, #00477D)"
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />

            <Flex
              px={6}
              pt={6}
              pb={4}
              align="center"
              gap={4}
            >
              {/* Icon with gradient background */}
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                align="center"
                justify="center"
                boxShadow="0 4px 14px rgba(0,71,125,0.3)"
              >
                <RiMessage3Line size={28} color="white" />
              </Flex>

              <Box flex={1}>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="#1a1a2e"
                  letterSpacing="-0.02em"
                >
                  Start New Conversation
                </Text>
                <Text
                  fontSize="14px"
                  color="#64748b"
                  mt={0.5}
                >
                  Chat with AI personas
                </Text>
              </Box>

              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody
            flex={1}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            px={6}
            py={4}
          >
            {dtTypeAccessLoading ? (
              <Flex justify="center" align="center" height="100%">
                <Spinner color={HelivanColor.text.helivanBlue} />
              </Flex>
            ) : (
              <Stack spacing={4} width="100%" height="full" overflow="hidden">
                <Stack spacing={2} p={1}>
                  <Text
                    fontSize="13px"
                    fontWeight={600}
                    color="#475569"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    Select persona type
                  </Text>
                  <HStack spacing={1}>
                    <HelivanSelector
                      options={dtTypes.map((dtType) => ({
                        key: dtType,
                        value: getDtTypeString(dtType),
                      }))}
                      onSelect={setSelectedDtType}
                      selectedOption={selectedDtType}
                    />
                  </HStack>
                </Stack>
                {digitalTwinsLoading ? (
                  <Flex justify="center" py={4}>
                    <Spinner color={HelivanColor.text.helivanBlue} />
                  </Flex>
                ) : (
                  digitalTwins.length > 0 && (
                    <>
                      <Stack spacing={2}>
                        <Text
                          fontSize="13px"
                          fontWeight={600}
                          color="#475569"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          Select conversation type
                        </Text>
                        <HStack spacing={2}>
                          <HStack
                            onClick={() =>
                              switchSelectionType(ConversationType.Persona)
                            }
                            bg={
                              chatType === ConversationType.Persona
                                ? 'rgba(0,71,125,0.1)'
                                : '#f8fafc'
                            }
                            spacing={2}
                            _hover={{ bg: 'rgba(0,71,125,0.08)' }}
                            px={3}
                            py={2}
                            borderRadius="10px"
                            cursor="pointer"
                            border="2px solid"
                            borderColor={chatType === ConversationType.Persona ? '#00477D' : 'transparent'}
                            transition="all 0.2s"
                          >
                            <MdOutlinePerson
                              size={18}
                              color={chatType === ConversationType.Persona ? '#00477D' : '#64748b'}
                            />
                            <Text
                              fontSize="14px"
                              fontWeight={600}
                              color={chatType === ConversationType.Persona ? '#00477D' : '#64748b'}
                            >
                              1-1
                            </Text>
                          </HStack>
                          <HStack
                            onClick={() =>
                              switchSelectionType(ConversationType.Panel)
                            }
                            bg={
                              chatType === ConversationType.Panel
                                ? 'rgba(0,71,125,0.1)'
                                : '#f8fafc'
                            }
                            spacing={2}
                            _hover={{ bg: 'rgba(0,71,125,0.08)' }}
                            px={3}
                            py={2}
                            borderRadius="10px"
                            cursor="pointer"
                            border="2px solid"
                            borderColor={chatType === ConversationType.Panel ? '#00477D' : 'transparent'}
                            transition="all 0.2s"
                          >
                            <MdOutlinePeople
                              size={18}
                              color={chatType === ConversationType.Panel ? '#00477D' : '#64748b'}
                            />
                            <Text
                              fontSize="14px"
                              fontWeight={600}
                              color={chatType === ConversationType.Panel ? '#00477D' : '#64748b'}
                            >
                              Panel
                            </Text>
                          </HStack>
                          <HStack
                            onClick={() =>
                              switchSelectionType(ConversationType.Demographic)
                            }
                            bg={
                              chatType === ConversationType.Demographic
                                ? 'rgba(0,71,125,0.1)'
                                : '#f8fafc'
                            }
                            spacing={2}
                            _hover={{ bg: 'rgba(0,71,125,0.08)' }}
                            px={3}
                            py={2}
                            borderRadius="10px"
                            cursor="pointer"
                            border="2px solid"
                            borderColor={chatType === ConversationType.Demographic ? '#00477D' : 'transparent'}
                            transition="all 0.2s"
                          >
                            <TbWorld
                              size={18}
                              color={chatType === ConversationType.Demographic ? '#00477D' : '#64748b'}
                            />
                            <Text
                              fontSize="14px"
                              fontWeight={600}
                              color={chatType === ConversationType.Demographic ? '#00477D' : '#64748b'}
                            >
                              Crowd
                            </Text>
                          </HStack>
                        </HStack>
                      </Stack>

                      <Box overflow="auto" flex="1" borderRadius="12px">
                        {chatType === ConversationType.Demographic ? (
                          selectedDtType && (
                            <DemographicSelector
                              dtType={selectedDtType}
                              type={chatType}
                              maxSelections={6}
                              onSelectionChange={handleDemographicChange}
                            />
                          )
                        ) : (
                          <PersonaSelector
                            onSelectionChange={handleSelectionChange}
                            allPersonas={digitalTwins}
                            type={chatType}
                          />
                        )}
                      </Box>

                      {isLoading && (
                        <Flex width="full" justifyContent="center">
                          <CreateChatLoading isLoading={true} size="sm" />
                        </Flex>
                      )}

                      <InputGroup>
                        <Input
                          bg="#f8fafc"
                          px={5}
                          py={4}
                          width="full"
                          borderRadius="14px"
                          height="54px"
                          border="2px solid"
                          borderColor="#e2e8f0"
                          fontSize="15px"
                          color="#1a1a2e"
                          _placeholder={{ color: '#94a3b8' }}
                          _hover={{ borderColor: '#cbd5e1' }}
                          _focus={{
                            borderColor: '#00477D',
                            boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                            bg: 'white',
                          }}
                          transition="all 0.2s"
                          value={activeMessage}
                          onChange={(e) => setActiveMessage(e.target.value)}
                          placeholder={`Ask ${chatType === ConversationType.Persona ? 'me' : 'us'} anything!`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
                              e.preventDefault();
                              handleCreateAndSend();
                            }
                          }}
                          isDisabled={createConversationLoading}
                        />

                        <InputRightElement
                          height="full"
                          alignItems="center"
                          pr={2}
                          onClick={
                            canSubmit && !isLoading
                              ? handleCreateAndSend
                              : undefined
                          }
                          cursor={
                            canSubmit && !isLoading ? 'pointer' : 'not-allowed'
                          }
                        >
                          <Box
                            opacity={canSubmit && !isLoading ? 1 : 0.5}
                            transition="all 0.2s"
                            _hover={canSubmit && !isLoading ? { transform: 'scale(1.1)' } : {}}
                          >
                            <IoArrowUpCircleOutline
                              size={32}
                              color="#00477D"
                            />
                          </Box>
                        </InputRightElement>
                      </InputGroup>
                    </>
                  )
                )}
              </Stack>
            )}
          </ModalBody>
        </Box>
      </ModalContent>
    </Modal>
  );
};
